import { formatDate } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const noop = () => {
};

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RfrDatePickerComponent),
  multi: true
};

@Component({
  selector: 'rfr-date-picker',
  templateUrl: './rfr-date-picker.component.html',
  styleUrls: ['./rfr-date-picker.component.scss'],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})

export class RfrDatePickerComponent implements ControlValueAccessor, OnInit {
  private datetimeElement: any;
  private monthYearButtonClicked = false;
  id = Math.floor(100000 + Math.random() * 900000);
  date_event: any =  null;
  @Input() readonly = false;
  @Input() class = "";
  @Input() labelText = "";
  @Input() isRequired = false;
  @Input({ required: false }) format = 'DD-MM-YYYY';
  @Input({ required: false }) placeholder = '';
  @Input() maxToday: boolean = false;
  @Input({ required: false }) minDate: any;
  @Input() maxDate = new Date("2199/12/31");
  @Input() pattern: string = '';
  @Input() errorMessage: string = "";
  @Input() error: boolean = false;
  @Input({ required: false }) min!: number;
  @Input({ required: false }) max!: number;
  @Input() maxLength: number = 255;
  @Input() style: any = "";
  @Input() disabled: any = false;
  @Input() showIcon: any = true;
  todayDate: any;
  minDayDate: any;
  @Output() onDatePicked = new EventEmitter<string>();


  constructor(private el: ElementRef, private renderer: Renderer2) {
    const timeElapsed = Date.now();
    const today = new Date(timeElapsed);
    this.date_event = today.toISOString();
  }

  ngAfterViewInit() {
    this.datetimeElement = this.el.nativeElement.querySelector('ion-datetime');
    this.observeDOMChanges();
  }

  observeDOMChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          this.checkForMonthYearView();
        }
      });
    });

    observer.observe(this.datetimeElement.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  checkForMonthYearView() {
    const shadowRoot = this.datetimeElement.shadowRoot;
    this.datetimeElement && this.renderer.setStyle(this.datetimeElement, 'margin', '20px auto');
    const monthYearButton = shadowRoot.querySelector('.calendar-month-year');
    if (monthYearButton && !this.monthYearButtonClicked) {
      this.addEventListenersToMonthYearView(monthYearButton);
    }
  }

  addEventListenersToMonthYearView(monthYearButton: Element) {
    monthYearButton.addEventListener('click', () => {
      if (!this.monthYearButtonClicked) {
        this.addCustomButtonsToShadowDom();
        this.monthYearButtonClicked = true;
      }
    });
  }

  addCustomButtonsToShadowDom() {
    requestAnimationFrame(() => {
      const shadowRoot = this.datetimeElement.shadowRoot;
      const footer = shadowRoot.querySelector('.datetime-footer');
      const calendar = shadowRoot.querySelector('.datetime-calendar');

      if (calendar) {
        calendar.style.fontWeight = 'bold'
      }

      if (footer) {
        footer.style.display = 'block';
      }
    });
  }

  onCancel() {
    this.datetimeElement.cancel();
  }

  onConfirm() {
    this.datetimeElement.confirm();
  }

  ngOnInit() {
    if (this.minDate) {
      this.minDayDate = formatDate(this.minDate, 'yyyy-MM-dd', 'en');
    }

    if (this.maxDate) {
      this.todayDate = formatDate(this.maxDate, 'yyyy-MM-dd', 'en');
    }
  }

  // The internal data model
  innerValue: any = '';

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  // Get accessor
  get value(): any {
    return this.innerValue;
  }

  // Set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);      
    }
  }

  ngOnChanges(change: SimpleChanges) {
    if (change['maxDate']) {
      this.todayDate = formatDate(this.maxDate, 'yyyy-MM-dd', 'en');      
    }
  }

  // Set touched on blur
  onBlur() {
    this.onTouchedCallback();
  }

  // From ControlValueAccessor interface
  writeValue(value: any) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      // Set value for date_event
      if (this.innerValue) {
        this.date_event = this.formatForDatepicker(this.innerValue);
      } else {
        this.date_event = null
      }
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  datePick() {
    const data = this.date_event?.substring(0, 10);
    this.value = this.formatForDisplay(data);
    if(!this.value) this.value = '';

    this.onDatePicked.emit(this.value); // Emit the new date value
  }

  clearDate() {
    this.innerValue = null;
    this.date_event = '';
    this.value = '';
  }

  formatForDisplay(value: string) {
    if (!!value) {
      if (this.format == 'DD/MM/YYYY') {
        const splitValues = value.split('-');
        return `${splitValues[2]}/${splitValues[1]}/${splitValues[0]}`;
      } else if (this.format == 'DD-MM-YYYY') {
        const splitValues = value.split('-');
        return `${splitValues[2]}-${splitValues[1]}-${splitValues[0]}`;
      }
    }
    return value;
  }

  formatForDatepicker(value: string) {
    if (this.format == 'DD/MM/YYYY') {
      const splitValues = value.split('/');
      return `${splitValues[2]}-${splitValues[1]}-${splitValues[0]}`;
    } else if (this.format == 'DD-MM-YYYY') {
      const splitValues = value.split('-');
      return `${splitValues[2]}-${splitValues[1]}-${splitValues[0]}`;
    }
    return value;
  }
}
